/**
 * BaseNode — shared canvas node card.
 *
 * Phase 4.1 port of design/kaiva-canvas/project/components/canvas.jsx Node().
 * Visual contract: fixed 232px card on Cream with a hairline border, a 4px
 * type-colored left rail, and a body containing meta · title · description ·
 * detail chips · connection footer. Type variants only choose `canvasNodeType`
 * + optionally pass a step number / sub-type — all visual differentiation is
 * driven by `getTypeMeta` (rail color, soft tint, type badge color).
 *
 * State indicators preserved from the legacy node:
 *   - staged       → dashed sienna border, sienna rail, radial glow, "proposed" pill
 *   - existing     → dashed border + 0.75 opacity (legacy "Vorwissen" feature, kept silent visually)
 *   - conflicting  → warning border + highlight pulse
 *   - selected     → foreground border + ring (NodeActionContext.activeNodeId)
 *   - focused      → focusPulse one-shot (NodeActionContext.focusedNodeId)
 *   - highlighted  → highlightPulse + sienna ring (NodeActionContext.highlightedNodeIds)
 *
 * Staged accept/dismiss UX moved to the bottom-bar StagedNodeReviewer in
 * Phase 5.1; the per-node popup that used to live here is gone.
 */

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Handle, Position } from '@xyflow/react';
import { motion, useAnimationControls } from 'framer-motion';
import { Link2, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TYPE_META, normalizeNodeType, type NodeType } from '@/lib/nodeTypes';
import { useNodeAction } from '../NodeActionContext';

export type NodeDetailChip = { k: string; v: string };

interface BaseNodeProps {
  /** React Flow node ID */
  nodeId: string;
  /** Node label (primary text) */
  label: string;
  /** Optional description */
  description?: string;
  /** Whether this is new, existing (preloaded), or conflicting */
  sourceType: 'new' | 'existing' | 'conflicting' | 'ambiguous';
  /** Canvas display type — case-insensitive lookup against TYPE_META */
  canvasNodeType: string;
  /** Number of connected edges */
  edgeCount: number;
  /** Optional sub-type label (e.g. RULE, RISK for insights; ROLE for actors) */
  subType?: string;
  /** Optional inline detail chips ("SLA", "10 min" style) */
  details?: NodeDetailChip[];
  /** Whether this node is staged (provisional) */
  staged?: boolean;
  /** Number of additional context entries on this node */
  contextCount?: number;
  /** Optional badge slot rendered in the meta row (e.g. "Step 02") */
  badge?: ReactNode;
}

export function BaseNode({
  nodeId,
  label,
  description,
  sourceType,
  canvasNodeType,
  edgeCount,
  subType,
  details,
  staged = false,
  contextCount = 0,
  badge,
}: BaseNodeProps) {
  const [isHovered, setIsHovered] = useState(false);
  const {
    onNodeAction,
    activeNodeId,
    focusedNodeId,
    highlightedNodeIds,
  } = useNodeAction();

  // D.3: spring-bounce + glow fade when a staged proposal is accepted.
  // Holds the glow class for one transition cycle so the ::after pseudo
  // can run a one-shot fadeOut instead of vanishing with the className swap.
  const controls = useAnimationControls();
  const prevStagedRef = useRef(staged);
  const [isAccepting, setIsAccepting] = useState(false);
  useEffect(() => {
    if (prevStagedRef.current === true && staged === false) {
      setIsAccepting(true);
      void controls.start({
        scale: [1, 1.02, 1],
        transition: { duration: 0.22, ease: 'easeOut', times: [0, 0.4, 1] },
      });
      const t = setTimeout(() => setIsAccepting(false), 260);
      prevStagedRef.current = staged;
      return () => clearTimeout(t);
    }
    prevStagedRef.current = staged;
  }, [staged, controls]);

  const isExisting = sourceType === 'existing' || sourceType === 'ambiguous';
  const isConflicting = sourceType === 'conflicting';
  const isSelected = activeNodeId === nodeId;
  const isFocused = focusedNodeId === nodeId;
  const isHighlighted = highlightedNodeIds.has(nodeId);

  const type: NodeType | null = normalizeNodeType(canvasNodeType);
  const meta = type ? TYPE_META[type] : null;
  const TypeIcon = meta?.icon;

  const railColor = staged
    ? 'hsl(var(--sienna))'
    : isConflicting
      ? 'hsl(var(--kaiva-warning))'
      : meta
        ? `hsl(${meta.color})`
        : 'hsl(var(--border-strong))';

  const tintBackground: CSSProperties['background'] = staged
    ? 'hsl(var(--card))'
    : meta
      ? `hsl(${meta.color} / 0.06)`
      : 'hsl(var(--card))';

  const borderColor = staged
    ? 'hsl(var(--sienna) / 0.7)'
    : isConflicting
      ? 'hsl(var(--kaiva-warning))'
      : isSelected
        ? 'hsl(var(--foreground) / 0.5)'
        : 'hsl(var(--border-strong))';

  // motion.div manages transform (hover lift via whileHover, accept bounce via controls);
  // CSS still owns box-shadow, border-color, opacity transitions.
  const cardStyle: CSSProperties = {
    width: 232,
    background: tintBackground,
    border: `1px solid ${borderColor}`,
    borderStyle: staged || isExisting ? 'dashed' : 'solid',
    borderRadius: 12,
    color: 'hsl(var(--foreground))',
    boxShadow: isHovered ? 'var(--shadow-2)' : 'var(--shadow-1)',
    transition: 'box-shadow 150ms ease, border-color 200ms ease, opacity 350ms ease',
    opacity: isExisting ? 0.75 : 1,
  };

  const typeLabel = meta?.label ?? canvasNodeType ?? 'Node';

  return (
    <motion.div
      layout
      animate={controls}
      whileHover={{ y: -1 }}
      transition={{ type: 'spring', stiffness: 220, damping: 22 }}
      className={cn(
        'relative flex select-none overflow-visible cursor-pointer',
        (staged || isAccepting) && 'canvas-node-glow',
        isAccepting && 'canvas-node-glow-fading',
        isHighlighted && 'canvas-node-highlight-ring',
        isFocused && 'animate-[focusPulse_1s_ease-out_1]',
        isConflicting && 'animate-[highlightPulse_1.4s_ease-in-out_infinite]',
      )}
      style={cardStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* React Flow handles — kept on all four sides for edge routing */}
      <Handle type="target" position={Position.Left}   className="!h-1.5 !w-1.5 !border-none !bg-transparent" />
      <Handle type="source" position={Position.Right}  className="!h-1.5 !w-1.5 !border-none !bg-transparent" />
      <Handle type="target" position={Position.Top}    id="t" className="!h-1.5 !w-1.5 !border-none !bg-transparent" />
      <Handle type="source" position={Position.Bottom} id="b" className="!h-1.5 !w-1.5 !border-none !bg-transparent" />

      {/* 4px type-colored rail */}
      <div
        aria-hidden="true"
        className="flex-shrink-0 rounded-l-[12px]"
        style={{ width: 4, background: railColor }}
      />

      {/* Body */}
      <div className="min-w-0 flex-1 px-3 py-2.5">
        {/* Meta row: type badge · subtype · proposed */}
        <div className="mb-1.5 flex items-center gap-1.5">
          {meta && TypeIcon && (
            <span
              className="inline-flex items-center gap-1 rounded-[5px] px-1.5 py-[2px] kv-eyebrow text-[9.5px] font-semibold"
              style={{
                background: `hsl(${meta.color} / 0.12)`,
                color: `hsl(${meta.color})`,
                lineHeight: 1,
              }}
            >
              <TypeIcon className="h-[11px] w-[11px]" strokeWidth={1.75} />
              {typeLabel}
            </span>
          )}
          {subType && (
            <span
              className="inline-flex items-center rounded-[5px] px-1.5 py-[2px] text-[9.5px] font-medium tracking-[0.02em]"
              style={{
                background: 'hsl(var(--muted))',
                color: 'hsl(var(--muted-foreground))',
                lineHeight: 1,
              }}
            >
              {subType}
            </span>
          )}
          {badge && <span className="inline-flex items-center">{badge}</span>}
          {staged && (
            <span
              className="ml-auto inline-flex items-center rounded-[5px] px-1.5 py-[2px] text-[9.5px] font-medium lowercase tracking-[0.04em]"
              style={{
                background: 'hsl(var(--sienna-soft))',
                color: 'hsl(var(--sienna-deep))',
                lineHeight: 1,
              }}
            >
              proposed
            </span>
          )}
        </div>

        {/* Title */}
        <div
          className="break-words"
          style={{
            fontWeight: 600,
            fontSize: 13.5,
            lineHeight: 1.3,
            letterSpacing: '-0.01em',
            color: 'hsl(var(--foreground))',
          }}
        >
          {label}
        </div>

        {/* Description */}
        {description && (
          <div
            className="mt-1"
            style={{
              fontSize: 11.5,
              lineHeight: 1.45,
              color: 'hsl(var(--muted-foreground))',
            }}
          >
            {description}
          </div>
        )}

        {/* Key/value detail chips */}
        {details && details.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {details.map((d, i) => (
              <span
                key={`${d.k}-${i}`}
                className="inline-flex items-center gap-1 rounded px-1.5 py-[2px] tabular-nums"
                style={{
                  fontSize: 10.5,
                  lineHeight: 1.3,
                  background: 'hsl(var(--muted) / 0.7)',
                  border: '1px solid hsl(var(--border))',
                }}
              >
                <span
                  className="kv-eyebrow"
                  style={{
                    fontSize: 9.5,
                    color: 'hsl(var(--muted-foreground))',
                    letterSpacing: '0.04em',
                  }}
                >
                  {d.k}
                </span>
                <span style={{ fontWeight: 500, color: 'hsl(var(--foreground))' }}>{d.v}</span>
              </span>
            ))}
          </div>
        )}

        {/* Connections footer */}
        {!staged && edgeCount > 0 && (
          <div
            className="mt-2 flex items-center gap-1 pt-[7px]"
            style={{
              borderTop: '1px dashed hsl(var(--border))',
              fontSize: 10.5,
              color: 'hsl(var(--muted-foreground))',
              letterSpacing: '0.02em',
            }}
          >
            <Link2 className="h-2.5 w-2.5 opacity-70" />
            <span>
              {edgeCount} connection{edgeCount > 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {/* Context badge (top-left) — sienna circle with note count */}
      {!staged && contextCount > 0 && (
        <div
          className="absolute z-[3] inline-flex items-center justify-center rounded-full tabular-nums"
          style={{
            left: -6,
            top: -6,
            width: 18,
            height: 18,
            background: 'hsl(var(--sienna))',
            color: 'white',
            fontSize: 10,
            fontWeight: 700,
            border: '2px solid hsl(var(--background))',
          }}
          title={`${contextCount} note${contextCount > 1 ? 's' : ''} added by you`}
        >
          {contextCount}
        </div>
      )}

      {/* Add-context pill (top-right) — visible on hover or when selected */}
      {!staged && !isExisting && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNodeAction(nodeId, label, canvasNodeType);
          }}
          className={cn(
            'absolute z-[4] inline-flex items-center gap-1 rounded-full transition-all duration-150',
            isHovered || isSelected ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
          )}
          style={{
            right: 6,
            top: -10,
            padding: '3px 8px 3px 6px',
            background: 'hsl(var(--background))',
            border: '1px solid hsl(var(--border))',
            color: 'hsl(var(--muted-foreground))',
            fontSize: 10,
            letterSpacing: '0.02em',
            boxShadow: 'var(--shadow-1)',
          }}
          aria-label={`Add context about ${label}`}
          title="Add something Kaiva missed"
        >
          <Pencil className="h-2.5 w-2.5 opacity-80" />
          add context
        </button>
      )}

    </motion.div>
  );
}
